// Thin WebSocket client for game-dom-ws-server (default ws://localhost:3163).
//
// Protocol mirrors apps/game-dom-demo/src/lib/ws-publisher in blueforge-org —
// hello → subscribe `game-dom-events` → emit snapshot events.
//
// Tolerates server-down: silently retries with exponential backoff.

import type { GameSnapshot } from '@aod/protocol';

export type WsConnectionState = 'connecting' | 'open' | 'closed' | 'errored';

export interface GameDomWsBridgeOptions {
  readonly url: string;
  readonly appId: string;
  readonly onState?: (s: WsConnectionState) => void;
  readonly onIntent?: (raw: unknown) => void;
}

export class GameDomWsBridge {
  private ws: WebSocket | null = null;
  private retryMs = 500;
  private destroyed = false;
  private readonly maxRetryMs = 15_000;

  state: WsConnectionState = 'closed';

  constructor(private readonly opts: GameDomWsBridgeOptions) {}

  connect(): void {
    if (this.destroyed) return;
    this.setState('connecting');
    try {
      this.ws = new WebSocket(this.opts.url);
    } catch (err) {
      console.warn('[ws] construct failed', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.addEventListener('open', () => {
      this.setState('open');
      this.retryMs = 500;
      this.send({
        type: 'hello',
        client: { name: this.opts.appId, version: '0.0.0' },
      });
      this.send({
        type: 'subscribe',
        topics: { events: { types: ['intent', 'trigger'] } },
      });
    });

    this.ws.addEventListener('close', () => {
      this.setState('closed');
      if (!this.destroyed) this.scheduleReconnect();
    });

    this.ws.addEventListener('error', () => {
      this.setState('errored');
      // close handler will fire too — don't double-schedule.
    });

    this.ws.addEventListener('message', (e) => {
      try {
        const msg = JSON.parse(e.data as string);
        if (msg?.type === 'intent') this.opts.onIntent?.(msg);
      } catch {
        /* ignore malformed */
      }
    });
  }

  publishSnapshot(snapshot: GameSnapshot): void {
    this.send({
      type: 'event',
      event: {
        id: `snap_${snapshot.frame}`,
        type: 'snapshot',
        source: this.opts.appId,
        timestamp: snapshot.timestamp,
        payload: { snapshot },
      },
    });
  }

  disconnect(): void {
    this.destroyed = true;
    this.ws?.close();
    this.ws = null;
    this.setState('closed');
  }

  private send(payload: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(payload));
    } catch (err) {
      console.warn('[ws] send failed', err);
    }
  }

  private setState(s: WsConnectionState) {
    if (this.state === s) return;
    this.state = s;
    this.opts.onState?.(s);
  }

  private scheduleReconnect() {
    if (this.destroyed) return;
    const delay = this.retryMs;
    this.retryMs = Math.min(this.maxRetryMs, this.retryMs * 2);
    setTimeout(() => this.connect(), delay);
  }
}
